import PGPlusFlow from '@/src/modules/pg-plus/PGPlusFlow'
import { GetStaticPropsContext } from 'next'
import { BRAND_PAGE } from '@/src/modules/pg-plus/constants'
import { GiftQuantityProvider } from '@/src/common/contexts/GiftQuantityContext'
import {
    BasicGiftCardProvider,
    useBasicGiftCardContext,
} from '@/src/common/contexts/BasicGiftCardContext'
import { getStaticPropsDataForPGPlus } from '@/src/modules/pg-plus/getStaticPropsForPGPlus'
import { findBrandsPgcEnabled } from '@/prisma/repository/brandRepository'
import getGypMerchantDenomMinMax from '@/src/common/utilities/getGypMerchantDenomMinMax'
import { IClosedLoopBrand } from '@/src/modules/pg-plus/types'
import DefaultPartnerStepHeader from '@/src/modules/partners/DefaultPartnerStepHeader'
import isEmpty from 'lodash/isEmpty'

const ConsumerBrandPage = ({
    builderBrand,
    brandData,
    trendingItems,
    minMaxFixedDenoms,
}: IClosedLoopBrand) => {
    const { partnerData } = useBasicGiftCardContext()
    const showMobileNav =
        isEmpty(partnerData) ||
        (partnerData?.programConfig?.show_mobile_nav ?? true)

    return (
        <>
            {partnerData && (
                <DefaultPartnerStepHeader
                    partnerData={partnerData}
                    currentStep={1}
                    showCart={true}
                    showMobileNav={showMobileNav}
                />
            )}
            <PGPlusFlow
                builderBrand={builderBrand}
                brandData={brandData}
                trendingItems={trendingItems}
                minMaxFixedDenoms={minMaxFixedDenoms}
            />
        </>
    )
}

const ConsumerBrandPageWithProviders = ({
    builderBrand,
    brandData,
    predesigns,
    productUuid,
    emailDeliveryOptionUuid,
    textDeliveryOptionUuid,
    trendingItems,
    minMaxFixedDenoms,
    partnerData,
    customerAttributionId,
    whiteLabelIdentifiers,
}: IClosedLoopBrand) => {
    return (
        <BasicGiftCardProvider
            predesigns={predesigns}
            partnerData={partnerData}
            whiteLabelIdentifiers={whiteLabelIdentifiers}
            defaultDenomination={25}
        >
            <GiftQuantityProvider
                emailDeliveryOptionUuid={emailDeliveryOptionUuid}
                textDeliveryOptionUuid={textDeliveryOptionUuid}
                productUuid={productUuid}
                customerAttributionId={customerAttributionId}
            >
                <ConsumerBrandPage
                    builderBrand={builderBrand}
                    brandData={brandData}
                    predesigns={predesigns}
                    productUuid={productUuid}
                    textDeliveryOptionUuid={textDeliveryOptionUuid}
                    emailDeliveryOptionUuid={emailDeliveryOptionUuid}
                    trendingItems={trendingItems}
                    minMaxFixedDenoms={minMaxFixedDenoms}
                />
            </GiftQuantityProvider>
        </BasicGiftCardProvider>
    )
}

export default ConsumerBrandPageWithProviders

export async function getStaticProps({
    params,
}: GetStaticPropsContext<{ slug: string }>) {
    const brandSlug = params?.slug ?? ''

    const staticPropsData = await getStaticPropsDataForPGPlus({
        brandSlug: brandSlug,
    })

    if (!staticPropsData) {
        return {
            redirect: {
                destination: BRAND_PAGE,
                statusCode: 301,
            },
        }
    }

    const { brandData, isEnabled, builderBrand } = staticPropsData
    const minMaxFixedDenoms = await getGypMerchantDenomMinMax(brandData)

    // if the brand doesn't exist in our database, redirect to the brands page
    if (!brandData?.brandUuid || !isEnabled) {
        if (process.env.npm_lifecycle_event === 'build')
            return {
                notFound: true,
            }

        return {
            redirect: {
                destination: BRAND_PAGE,
                statusCode: 301,
            },
        }
    }

    const brandNameToDisplay: string = brandData?.name ?? 'merchant'
    const pageTitle: string = builderBrand?.pageTitle
    const metaDescription: string = builderBrand?.data?.metaDescription
        ? builderBrand?.data?.metaDescription
        : `Buy ${brandNameToDisplay} gift cards or eGift cards delivered fast.`

    return {
        props: {
            minMaxFixedDenoms,
            ...staticPropsData,
            content: {
                data: {
                    title:
                        pageTitle ||
                        `Buy ${brandNameToDisplay} Gift Cards Online`,
                    description: metaDescription,
                },
            },
        },
        revalidate: 60,
    }
}

export async function getStaticPaths() {
    const brands = await findBrandsPgcEnabled()

    return {
        paths: brands.map((brand) => `${BRAND_PAGE}/${brand.slug}`),
        fallback: 'blocking',
    }
}
